import { useMemo } from "react";
import { EXECUTION_STATUS } from "../../../../AutomationStudio/hooks/useReplayExecution";
import { EVALUATION_TOOL } from "../../../../AutomationStudio/constant";
import { getTotalSteps } from "../../../../AutomationStudio/utils";
import { getActiveFlatSteps } from "../../../../../features/automationStudioNew/planUtils";
import { resolveStepIndexForReplayLookup } from "../../leftPanel/steps/instructionHierarchyUtils";
import {
    getReplayUserFacingErrorMessage,
    getVerificationTabState,
} from "../../../../AutomationStudio/utils/stepExecutionDisplay";
import {
    shouldShowRecordingSkipUi,
    isSavedRecordedPlanIdleView,
    isReplayStepQueued,
    getRecordingSkipReason,
} from "../../../../AutomationStudio/utils/replayEligibility";
import { resolveReplayStepStatus } from "../../../../AutomationStudio/utils/replayProgressDisplay";
import { computeGenerationActiveFlatIndex } from "../../leftPanel/steps/instructionHierarchyUtils";

export function useStepEditorDisplayContext({
    step,
    localStep,
    instructions,
    isExecuting,
    isGenerating,
    isReplayExecuting,
    isLoadedFromTestcase,
    hasExecutedInSession,
    isReplayFlow,
    testCompleted = false,
    replayExecutionResults,
    replayProgressMap,
    replayCurrentStepIndex,
    selectedFlatStepIndex,
    showPreviewTab,
    hasStepMetaInfo,
    activeTab,
}) {
    const isSavedPlanIdle = useMemo(
        () => isSavedRecordedPlanIdleView({
            instructions,
            hasExecutedInSession,
            isRunning: isExecuting,
            isReplayExecuting,
            isGenerating,
        }),
        [instructions, hasExecutedInSession, isExecuting, isReplayExecuting, isGenerating]
    );

    const flatStepsForReplay = useMemo(
        () => getActiveFlatSteps(instructions ?? []),
        [instructions]
    );

    if (!step) {
        return {
            showResultsBackButton: false,
            isVerificationStep: false,
            stepIndexForLookup: selectedFlatStepIndex,
            replayStatus: null,
            isRecordingSkipLocked: false,
            isEditorLocked: isExecuting,
            recordingSkipReason: '',
            replayDetail: null,
            replayResult: null,
            replayEval: null,
            executedCodeStrategyType: null,
            displayStep: null,
            currentTool: null,
            showSelectorsTab: false,
            isEvaluationTool: false,
            metaExtra: hasStepMetaInfo ? 1 : 0,
            effectiveMaxTabIndex: 0,
            safeActiveTab: 0,
            verificationUiState: null,
            readOnlyEditor: false,
            totalSteps: getTotalSteps(instructions),
            hasPrevious: false,
            hasNext: false,
            statusOptions: { isReplayFlow, isSavedPlanIdle, replayStatus: null, instructions },
            detailForUi: null,
            shouldMaskRecordingOutcome: false,
        };
    }

    let hasVerificationData = (
        (step.expectedOutcome !== undefined && step.actualOutcome !== undefined) ||
        (step.expected_outcome !== undefined && step.actual_outcome !== undefined) ||
        step.passed !== undefined ||
        (step.executionGroup?.mainAction?.expectedOutcome !== undefined &&
            step.executionGroup?.mainAction?.actualOutcome !== undefined)
    );

    const isPlanOnlyLoadedFromTestcase = isLoadedFromTestcase && !hasExecutedInSession;

    const isLiveRunActive = isExecuting && !isReplayExecuting;

    // Show "Results" back button when:
    // - AI step execution is currently running (mid execution), or
    // - execution completed in this session (testCompleted),
    // but NEVER show it when we're only loading a saved plan from a testcase
    // and the user hasn't executed anything in this session.
    const showResultsBackButton = !isPlanOnlyLoadedFromTestcase && (
        isExecuting || testCompleted || (isReplayFlow && hasExecutedInSession)
    );

    // For plan-only loaded executions, hide historical verification data until
    // the user actually runs the steps in this session.
    if (isPlanOnlyLoadedFromTestcase || isSavedPlanIdle) {
        hasVerificationData = false;
    }

    const stepIndexForLookup = resolveStepIndexForReplayLookup(
        step,
        selectedFlatStepIndex,
        flatStepsForReplay
    );

    const replayStatusFromMap = (typeof stepIndexForLookup === 'number' && replayProgressMap)
        ? resolveReplayStepStatus(step, stepIndexForLookup, replayProgressMap, isReplayFlow, instructions)
        : null;
    const shouldInferActiveReplayStep =
        isReplayExecuting &&
        typeof stepIndexForLookup === 'number' &&
        (
            replayCurrentStepIndex === stepIndexForLookup ||
            (replayCurrentStepIndex == null && stepIndexForLookup === 0)
        ) &&
        (
            replayStatusFromMap == null ||
            replayStatusFromMap === EXECUTION_STATUS.NEW ||
            replayStatusFromMap === EXECUTION_STATUS.PENDING
        );
    const replayStatus = shouldInferActiveReplayStep
        ? EXECUTION_STATUS.IN_PROGRESS
        : replayStatusFromMap;

    const hasReplayRunForStep = !!replayStatus && ![EXECUTION_STATUS.NEW, EXECUTION_STATUS.PENDING].includes(replayStatus);

    // During replay, hide recording/generation verification fields until this step runs.
    if (isReplayFlow && !hasReplayRunForStep) {
        hasVerificationData = false;
    }

    const isVerificationStep = (step.tool === EVALUATION_TOOL) || hasVerificationData;

    const isRecordingSkipLocked = shouldShowRecordingSkipUi(step, instructions, {
        isReplayFlow,
        isSavedPlanIdle,
        isLiveRunActive,
        replayStatus,
    });
    const isEditorLocked = isExecuting || isRecordingSkipLocked;
    const recordingSkipReason = isRecordingSkipLocked ? getRecordingSkipReason(step.status) : '';

    // When replay results exist for this step (identified by flat index), prefer those
    // for display-only fields like status, duration, outcomes, confidence, and reason.
    // For a Replay session, we only surface those once the current replay has actually
    // run the step (status PASSED/FAILED/SKIPPED/IN_PROGRESS). On initial load of a
    // saved execution, we keep the UI in a "pending" state so we don't show stale
    // historical verification until the new replay runs.
    const hasReplayStepDetails =
        replayExecutionResults?.stepDetails &&
        Object.keys(replayExecutionResults.stepDetails).length > 0;
    const isReplaySession = isReplayFlow && (hasReplayStepDetails || isReplayExecuting);
    const hasReplayContext = !!(hasReplayStepDetails && typeof stepIndexForLookup === 'number');
    const replayDetail = hasReplayContext
        ? replayExecutionResults.stepDetails[stepIndexForLookup]
        : null;
    const replayResult = replayDetail?.result;
    const replayEval = replayResult?.result;

    const executedCodeStrategyType =
        replayDetail?.result?.executedStrategy?.type ??
        replayDetail?.result?.executed_strategy?.type ??
        replayResult?.result?.executedStrategy?.type ??
        replayResult?.result?.executed_strategy?.type ??
        null;

    // Derive pass/fail from replay. For evaluateTestStep, `success` means the tool ran;
    // `passed` is the AI/assertion verdict — prefer `passed` over `success`.
    let derivedPassed;
    if (replayDetail) {
        const isEvalStep =
            step.tool === EVALUATION_TOOL ||
            step.executionGroup?.mainAction?.tool === EVALUATION_TOOL;
        if (isEvalStep) {
            const verdict = replayEval?.passed;
            if (typeof verdict === 'boolean') {
                derivedPassed = verdict;
            } else if (verdict === 'false') {
                derivedPassed = false;
            } else if (replayStatus === EXECUTION_STATUS.FAILED) {
                derivedPassed = false;
            } else if (replayStatus === EXECUTION_STATUS.PASSED) {
                derivedPassed = typeof replayEval?.success === 'boolean' ? replayEval.success : true;
            } else {
                derivedPassed = undefined;
            }
        } else {
            derivedPassed =
                typeof replayEval?.success === 'boolean'
                    ? replayEval.success
                    : replayStatus === EXECUTION_STATUS.PASSED;
        }
    } else {
        derivedPassed = undefined;
    }

    let displayStep = step;

    // Treat as "plan-only" for Replay flows **only** when the current replay has not
    // yet produced a real status for this step. This keeps loaded executions pending
    // (no Completed / Verification Passed / Preview) until the user actually runs reply.
    const isPlanOnlyLoad = isReplaySession && !hasReplayRunForStep;
    const isReplayQueuedStep = isReplayStepQueued(isReplayFlow, replayStatus, isRecordingSkipLocked);
    const isReplayStepActive =
        isReplayExecuting && replayStatus === EXECUTION_STATUS.IN_PROGRESS;
    const isReplayOutcomeReady =
        replayStatus === EXECUTION_STATUS.PASSED ||
        replayStatus === EXECUTION_STATUS.FAILED ||
        replayStatus === EXECUTION_STATUS.SKIPPED;
    const shouldMaskRecordingOutcome =
        !isReplayStepActive &&
        !isReplayOutcomeReady &&
        (isPlanOnlyLoadedFromTestcase ||
        isSavedPlanIdle ||
        isReplayQueuedStep ||
        (isReplayFlow && !hasReplayRunForStep));
    const statusOptions = { isReplayFlow, isSavedPlanIdle, replayStatus, instructions };
    const detailForUi =
        shouldMaskRecordingOutcome || (isReplayFlow && !isReplayOutcomeReady)
            ? null
            : replayDetail;

    if (shouldMaskRecordingOutcome) {
        displayStep = {
            ...step,
            status: isRecordingSkipLocked ? 'skipped' : 'pending',
            passed: undefined,
            duration: undefined,
            confidence: undefined,
            executedAt: undefined,
            actualOutcome: undefined,
            reason: undefined,
            result: undefined,
            resultMessage: undefined,
            executionGroup: step.executionGroup
                ? {
                    ...step.executionGroup,
                    mainAction: step.executionGroup.mainAction
                        ? {
                            ...step.executionGroup.mainAction,
                            status: isRecordingSkipLocked ? 'skipped' : 'pending',
                            passed: undefined,
                            actualOutcome: undefined,
                            reason: undefined,
                        }
                        : step.executionGroup.mainAction
                }
                : step.executionGroup
        };
    } else if (isReplayStepActive) {
        displayStep = {
            ...step,
            status: 'running',
            passed: undefined,
            duration: undefined,
            confidence: undefined,
            executedAt: undefined,
            actualOutcome: undefined,
            reason: undefined,
            result: undefined,
            resultMessage: undefined,
            executionGroup: step.executionGroup
                ? {
                    ...step.executionGroup,
                    mainAction: step.executionGroup.mainAction
                        ? {
                            ...step.executionGroup.mainAction,
                            status: 'running',
                            passed: undefined,
                            actualOutcome: undefined,
                            reason: undefined,
                        }
                        : step.executionGroup.mainAction
                }
                : step.executionGroup
        };
    } else if (isPlanOnlyLoad) {
        displayStep = {
            ...step,
            status: 'pending',
            passed: undefined,
            duration: undefined,
            confidence: undefined,
            executedAt: undefined,
            executionGroup: step.executionGroup
                ? {
                    ...step.executionGroup,
                    mainAction: step.executionGroup.mainAction
                        ? {
                            ...step.executionGroup.mainAction,
                            status: 'pending',
                            passed: undefined
                        }
                        : step.executionGroup.mainAction
                }
                : step.executionGroup
        };
    } else if (replayStatus === EXECUTION_STATUS.SKIPPED || (isRecordingSkipLocked && (replayStatus !== EXECUTION_STATUS.FAILED && replayStatus !== EXECUTION_STATUS.PASSED))) {
        // `passed` must be undefined (not false) so getEvaluationVerdict doesn't read
        // it as a failed-verdict candidate and downgrade the badge to "failed" for
        // evalTestType steps. The helper short-circuit on `status === 'skipped'` in
        // stepExecutionDisplay.js handles the rest.
        displayStep = {
            ...step,
            status: 'skipped',
            passed: undefined,
            duration: null,
            executedAt: null,
        };
    } else if (replayDetail && !isReplayQueuedStep) {
        // Derive confidence in the same way as other views (e.g. StepResultsTab)
        // so that the Verification tab and header show a consistent percentage
        // for both Generate AI runs and Replay executions.
        const effectiveConfidence =
            (typeof replayEval?.confidence === 'number' && replayEval.confidence) ??
            (typeof replayResult?.result?.confidence === 'number' && replayResult.result.confidence) ??
            (typeof replayResult?.details?.confidence === 'number' && replayResult.details.confidence) ??
            step.confidence;

        // Prefer evaluation/LLM copy over raw errorMessage (transport may cite wrong graph node).
        const replayErrorText =
            getReplayUserFacingErrorMessage(replayDetail) ??
            (() => {
                const replayErr = replayResult?.result?.errorMessage ?? replayResult?.result?.error;
                return typeof replayErr === 'string' ? replayErr : (replayErr?.message ?? replayErr);
            })();

        let baseStatus = replayStatus === EXECUTION_STATUS.PASSED ? 'success' :
                    replayStatus === EXECUTION_STATUS.FAILED ? 'failed' :
                        replayStatus === EXECUTION_STATUS.IN_PROGRESS ? 'running' :
                            replayStatus === EXECUTION_STATUS.PENDING || replayStatus === EXECUTION_STATUS.NEW
                                ? 'pending'
                                : replayDetail.status || step.status;
                            
        if ((step.tool === EVALUATION_TOOL || step.executionGroup?.mainAction?.tool === EVALUATION_TOOL) && (baseStatus === 'success' || baseStatus === 'completed')) {
            const evalPassed = replayEval?.passed ?? step.passed ?? step.args?.passed ?? step.details?.passed ?? step.executionGroup?.mainAction?.passed ?? step.executionGroup?.mainAction?.args?.passed ?? null;
            if (evalPassed === false || evalPassed === 'false') {
                baseStatus = 'failed';
            }
        }

        const planExpectedOutcome =
            step.expectedOutcome ??
            step.expected_outcome ??
            step.args?.expectedOutcome ??
            step.arguments?.expectedOutcome ??
            step.executionGroup?.mainAction?.expectedOutcome ??
            step.executionGroup?.mainAction?.expected_outcome ??
            step.executionGroup?.mainAction?.args?.expectedOutcome ??
            step.executionGroup?.mainAction?.arguments?.expectedOutcome ??
            undefined;

        displayStep = {
            ...step,
            status: baseStatus,
            // Prefer replay flow duration values (matching Results/Preview tabs),
            // and only fall back to the original saved step duration as a last resort.
            duration:
                replayDetail?.duration ??
                replayResult?.duration ??
                replayResult?.result?.duration ??
                replayResult?.durationMs ??
                step.duration,
            executedAt: replayResult?.executedAt ?? step.executedAt,
            confidence: effectiveConfidence,
            // IMPORTANT: never keep old "passed" from the plan once replay data exists.
            passed: derivedPassed,
            // Replay: keep expected outcome strictly from the saved plan — never substitute from replay results.
            expectedOutcome: planExpectedOutcome ?? replayEval?.expectedOutcome ?? step.expectedOutcome,
            runTimeExpectedOutcome: replayEval?.runTimeExpectedOutcome ?? step.runTimeExpectedOutcome,
            actualOutcome: isReplayOutcomeReady
                ? (replayEval?.actualOutcome ?? step.actualOutcome)
                : undefined,
            reason: isReplayOutcomeReady ? (replayEval?.reason ?? step.reason) : undefined,
            error: replayErrorText ?? step.error,
            executionGroup: {
                ...(step.executionGroup || {}),
                mainAction: {
                    ...(step.executionGroup?.mainAction || {}),
                    status:
                        replayStatus === EXECUTION_STATUS.PASSED ? 'success' :
                            replayStatus === EXECUTION_STATUS.FAILED ? 'failed' :
                                replayStatus === EXECUTION_STATUS.IN_PROGRESS ? 'running' :
                                    replayDetail.status || step.executionGroup?.mainAction?.status,
                    passed: derivedPassed,
                    expectedOutcome:
                        step.executionGroup?.mainAction?.expectedOutcome ??
                        step.executionGroup?.mainAction?.expected_outcome ??
                        step.executionGroup?.mainAction?.args?.expectedOutcome ??
                        step.executionGroup?.mainAction?.arguments?.expectedOutcome ??
                        planExpectedOutcome ?? replayEval?.expectedOutcome,
                    runTimeExpectedOutcome: replayEval?.runTimeExpectedOutcome ?? step.executionGroup?.mainAction?.runTimeExpectedOutcome,
                    actualOutcome: isReplayOutcomeReady
                        ? (replayEval?.actualOutcome ?? step.executionGroup?.mainAction?.actualOutcome)
                        : undefined,
                    reason: isReplayOutcomeReady
                        ? (replayEval?.reason ?? step.executionGroup?.mainAction?.reason)
                        : undefined
                }
            }
        };
    } else if (isReplaySession && replayStatus) {
        // When replay is running but we don't yet have fresh replayDetail for this step
        // (e.g. new replay started on a previously completed execution), derive a
        // display status purely from replayStatus so we never show stale "success"
        // while the current step is actually in progress.
        let mappedStatus = 'pending';
        switch (replayStatus) {
            case EXECUTION_STATUS.PASSED:
                mappedStatus = 'success';
                break;
            case EXECUTION_STATUS.FAILED:
                mappedStatus = 'failed';
                break;
            case EXECUTION_STATUS.IN_PROGRESS:
                mappedStatus = 'running';
                break;
            case EXECUTION_STATUS.SKIPPED:
                mappedStatus = 'skipped';
                break;
            case EXECUTION_STATUS.NEW:
            case EXECUTION_STATUS.PENDING:
            default:
                mappedStatus = 'pending';
                break;
        }
        displayStep = {
            ...step,
            status: mappedStatus,
            ...(mappedStatus === 'running'
                ? {
                    passed: undefined,
                    actualOutcome: undefined,
                    reason: undefined,
                    result: undefined,
                    confidence: undefined,
                    duration: undefined,
                }
                : {}),
        };
    }

    const generationActiveFlatIndex = computeGenerationActiveFlatIndex(
        instructions,
        isGenerating && isLiveRunActive
    );
    if (
        generationActiveFlatIndex != null &&
        typeof stepIndexForLookup === 'number' &&
        stepIndexForLookup === generationActiveFlatIndex &&
        !isReplayExecuting
    ) {
        displayStep = {
            ...displayStep,
            status: 'running',
            ...(displayStep.executionGroup?.mainAction
                ? {
                    executionGroup: {
                        ...displayStep.executionGroup,
                        mainAction: {
                            ...displayStep.executionGroup.mainAction,
                            status: 'running',
                        },
                    },
                }
                : {}),
        };
    }

    // XPath/Selectors tab only for tools that support element locators (matches Script Manager)
    // Use localStep.tool when available so the tab updates immediately when the user
    // changes the tool in the editor (Generate AI + manual edits).
    const currentTool = localStep?.tool || step.tool;
    // const showSelectorsTab = !isVerificationStep && toolSupportsElements(currentTool);
    const showSelectorsTab = false; // Selectors tab hidden — visible under Tool tab
    const isEvaluationTool = currentTool === EVALUATION_TOOL;

    // Preview tab only shown when step has been executed (plan replayed); hide if not executed
    const metaExtra = hasStepMetaInfo ? 1 : 0;
    const effectiveMaxTabIndex = isVerificationStep
        ? metaExtra
        : (!showPreviewTab
            ? (showSelectorsTab ? 2 : 1) + metaExtra
            : (showSelectorsTab ? 3 : 2) + metaExtra);
    const safeActiveTab = activeTab > effectiveMaxTabIndex ? 0 : activeTab;

    const verificationUiState = getVerificationTabState(displayStep, detailForUi, statusOptions);
    const readOnlyEditor = hasStepMetaInfo || isRecordingSkipLocked;

    // Calculate step position
    const totalSteps = getTotalSteps(instructions);

    // Then find current step number and instruction context
    let globalStepNumber = 0;
    let instructionNumber = 0;
    let localStepNumber = 0;
    let instructionText = '';
    let foundStep = false;

    instructions.forEach((inst, instIndex) => {
        if (inst.aiSteps && inst.aiSteps.length > 0) {
            inst.aiSteps.forEach((s, stepIndex) => {
                if (!foundStep) {
                    globalStepNumber++;
                    if (s.id === step.id) {
                        foundStep = true;
                        instructionNumber = instIndex + 1;
                        localStepNumber = stepIndex + 1;
                        instructionText = inst.text;
                    }
                }
            });
        }
    });

    const hasPrevious = globalStepNumber > 1;
    const hasNext = globalStepNumber < totalSteps;

    return {
        showResultsBackButton,
        isVerificationStep,
        stepIndexForLookup,
        replayStatus,
        isRecordingSkipLocked,
        isEditorLocked,
        recordingSkipReason,
        replayDetail,
        replayResult,
        replayEval,
        executedCodeStrategyType,
        displayStep,
        currentTool,
        showSelectorsTab,
        isEvaluationTool,
        metaExtra,
        effectiveMaxTabIndex,
        safeActiveTab,
        verificationUiState,
        readOnlyEditor,
        totalSteps,
        hasPrevious,
        hasNext,
        statusOptions,
        detailForUi,
        shouldMaskRecordingOutcome,
    };
}
